import { IconEdit, IconDeviceFloppy, IconX } from '@tabler/icons-react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { LanguageSelector } from '@/components/customComponents'

const MainTab = () => {
  const { t } = useTranslation()
  const [isEditMode, setIsEditMode] = useState(false)
  const [formData, setFormData] = useState({
    name: 'John Doe',
    email: 'john.doe@company.com',
    phone: '+1 (555) 123-4567',
  })
  const [originalData, setOriginalData] = useState(formData)

  const handleEdit = useCallback(() => {
    setIsEditMode(true)
    setOriginalData(formData)
  }, [formData])

  const handleCancel = useCallback(() => {
    setIsEditMode(false)
    setFormData(originalData)
  }, [originalData])

  const handleSave = useCallback(() => {
    setIsEditMode(false)
    setOriginalData(formData)
    // TODO: Save to backend
  }, [formData])

  const handleChange = useCallback(
    (field: string, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }))
    },
    []
  )

  return (
    <Card>
      <CardContent className="p-0">
        <div className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-display-s font-semibold text-black-500">
                {t('settings.userInfo.title')}
              </h2>
              <p className="text-text-s text-deepNavy-400 mt-1">
                {t('settings.userInfo.description')}
              </p>
            </div>
            <div className="flex gap-2">
              {!isEditMode ? (
                <Button onClick={handleEdit} size="default" variant="default">
                  <IconEdit className="h-4 w-4 mr-2" />
                  {t('common.buttons.edit')}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleCancel}
                    size="default"
                    variant="outline"
                  >
                    <IconX className="h-4 w-4 mr-2" />
                      {t('common.buttons.cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    size="default"
                    variant="default"
                  >
                    <IconDeviceFloppy className="h-4 w-4 mr-2" />
                      {t('settings.userInfo.saveChanges')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <Separator />

        <div className="p-6">
          <div className="space-y-6 max-w-2xl">
            <div>
              <label className="text-text-s font-medium text-deepNavy-400 mb-2 block">
                {t('settings.userInfo.fullName')}
              </label>
              {isEditMode ? (
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder={t('settings.userInfo.enterFullName')}
                />
              ) : (
                <p className="text-display-s text-black-500">{formData.name}</p>
              )}
            </div>

            <div>
              <label className="text-text-s font-medium text-deepNavy-400 mb-2 block">
                {t('settings.userInfo.emailAddress')}
              </label>
              {isEditMode ? (
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder={t('settings.userInfo.enterEmailAddress')}
                />
              ) : (
                <p className="text-display-s text-black-500">
                  {formData.email}
                </p>
              )}
            </div>

            <div>
              <label className="text-text-s font-medium text-deepNavy-400 mb-2 block">
                {t('settings.userInfo.phoneNumber')}
              </label>
              {isEditMode ? (
                <Input
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder={t('settings.userInfo.enterPhoneNumber')}
                />
              ) : (
                <p className="text-display-s text-black-500">
                  {formData.phone}
                </p>
              )}
            </div>
          </div>
        </div>

        <Separator />

        <div className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-display-s font-semibold text-black-500">
                {t('settings.language.title')}
              </h2>
              <p className="text-text-s text-deepNavy-400 mt-1">
                {t('settings.language.description')}
              </p>
            </div>
          </div>
        </div>

        <Separator />

        <div className="p-6">
          <div className="max-w-2xl">
            <label className="text-text-s font-medium text-deepNavy-400 mb-2 block">
              {t('settings.language.selectLanguage')}
            </label>
            <LanguageSelector className="w-48" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default MainTab

